import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auleloApi, auleloAuth } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AuleloLogin() {
  const [passkey, setPasskey] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    if (!passkey.trim()) return;
    setBusy(true);
    try {
      const { data } = await auleloApi.post("/auth", { passkey });
      auleloAuth.saveToken(data.token);
      toast.success(`Welcome, ${data.agent_name || "Aulelo"}`);
      if (data.first_login) {
        nav("/aulelo/first-login", { replace: true });
      } else {
        nav("/aulelo/console", { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Access denied";
      toast.error(detail);
      setPasskey("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center p-6 relative overflow-hidden">
      {/* CRT scanline backdrop */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "repeating-linear-gradient(0deg, rgba(0,255,140,0.03) 0px, rgba(0,255,140,0.03) 1px, transparent 1px, transparent 3px)"
      }} />
      <div className="absolute -top-40 -left-40 w-[60vw] h-[60vw] rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(circle, #00ff88 0%, transparent 60%)" }} />

      <form
        onSubmit={submit}
        data-testid="aulelo-login-form"
        className="relative max-w-md w-full border border-emerald-700/40 bg-black/80 backdrop-blur-xl shadow-[0_0_60px_rgba(0,255,140,0.08)]"
      >
        <div className="border-b border-emerald-700/30 px-6 py-4 flex items-center gap-3">
          <Lock className="w-4 h-4 text-emerald-400" />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-300">classified · agent gate</div>
        </div>

        <div className="px-6 py-8">
          <h1 className="font-mono text-3xl font-black tracking-tight text-emerald-200">AULELO</h1>
          <p className="text-xs text-zinc-500 mt-2 font-mono">Restricted briefing channel.<br/>Enter the passkey to proceed.</p>

          <label className="block mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">passkey</label>
          <Input
            type="password"
            autoFocus
            autoComplete="off"
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
            data-testid="aulelo-passkey-input"
            className="mt-2 rounded-none bg-black border-emerald-700/40 text-emerald-200 placeholder:text-zinc-700 focus-visible:ring-1 focus-visible:ring-emerald-500 h-12 font-mono tracking-widest"
            placeholder="••••••••"
          />

          <Button
            type="submit"
            disabled={busy || !passkey.trim()}
            data-testid="aulelo-login-submit"
            className="w-full mt-5 rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-[0.25em] text-xs h-12"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {busy ? "Verifying" : "Authenticate"}
          </Button>

          <div className="mt-6 border border-yellow-600/30 bg-yellow-900/10 p-3 text-[11px] text-yellow-200/80 flex gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-yellow-500" />
            <div>
              All attempts are audit-logged with IP. 5 failed attempts within 15 minutes trigger a lockout.
            </div>
          </div>
        </div>

        <div className="border-t border-emerald-700/30 px-6 py-3 flex items-center justify-between text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
          <span>session ttl · 4h</span>
          <span>bcrypt · jwt · audit</span>
        </div>
      </form>
    </div>
  );
}
